/**
 * ChartersPage — Reporte agregado de charters (alquiler de embarcaciones).
 * Endpoint: GET /turismo/charter/reportes/resumen?desde&hasta&empresa_id
 *
 * Muestra:
 *   - KPIs de estado (presupuestos, confirmados, embarcados, completados, cancelados)
 *   - Ingresos confirmados vs cobrados en el período
 *   - Tasa de conversión (confirmados o más / total generados)
 *   - Tabla por mes (cantidad + ingresos)
 *   - Tabla por embarcación (utilización %, ingresos)
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, CircularProgress, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  LinearProgress,
} from '@mui/material';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PaidIcon from '@mui/icons-material/Paid';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; }
  catch { return '1'; }
}
function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;
const fmtMes = (k) => {
  if (!k) return '';
  const [y, m] = k.split('-');
  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${meses[parseInt(m, 10) - 1]} ${y}`;
};

const initialRange = () => {
  const hasta = new Date();
  const desde = new Date();
  desde.setMonth(desde.getMonth() - 3);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { desde: iso(desde), hasta: iso(hasta) };
};

export default function ChartersPage() {
  const empresaId = getEmpresaId();
  const [rango, setRango] = useState(initialRange);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/charter/reportes/resumen?empresa_id=${empresaId}&desde=${rango.desde}&hasta=${rango.hasta}`,
        { headers: getAuthHeaders() }
      );
      if (r.ok) setData(await r.json());
    } catch (e) { console.error('[reportes charter]', e); }
    finally { setLoading(false); }
  }, [empresaId, rango]);

  useEffect(() => { cargar(); }, [cargar]);

  const set = (k) => (e) => setRango(p => ({ ...p, [k]: e.target.value }));

  const t = data?.totales || {};
  const kpis = [
    { icon: <DirectionsBoatIcon />, label: 'Presupuestos', value: t.presupuestos ?? 0, color: '#f59e0b' },
    { icon: <DirectionsBoatIcon />, label: 'Confirmados',  value: t.confirmados ?? 0,  color: '#1e40af' },
    { icon: <DirectionsBoatIcon />, label: 'Completados',  value: t.completados ?? 0,  color: '#166534' },
    { icon: <DirectionsBoatIcon />, label: 'Cancelados',   value: t.cancelados ?? 0,   color: '#991b1b' },
    { icon: <PaidIcon />,           label: 'Ingresos confirmados', value: fmtMoney(t.ingresos_confirmados), color: '#7c3aed', wide: true },
    { icon: <PaidIcon />,           label: 'Ingresos cobrados',    value: fmtMoney(t.ingresos_cobrados),    color: '#059669', wide: true },
    { icon: <TrendingUpIcon />,     label: 'Tasa de conversión',   value: `${t.tasa_conversion_pct ?? 0}%`, color: '#0891b2', wide: true },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
            <DirectionsBoatIcon sx={{ color: '#7c3aed' }} />
            Reporte de Charters
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            Alquiler de embarcaciones · rango, ingresos y utilización
          </Typography>
        </Box>
        <TextField size="small" type="date" label="Desde"
          InputLabelProps={{ shrink: true }}
          value={rango.desde} onChange={set('desde')} sx={{ width: 160 }}
        />
        <TextField size="small" type="date" label="Hasta"
          InputLabelProps={{ shrink: true }}
          value={rango.hasta} onChange={set('hasta')} sx={{ width: 160 }}
        />
      </Box>

      {loading || !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#7c3aed' }} />
        </Box>
      ) : (
        <>
          {/* KPIs */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {kpis.map(k => (
              <Grid item xs={6} sm={k.wide ? 4 : 3} md={k.wide ? 4 : 3} key={k.label}>
                <Paper sx={{ p: 2, borderLeft: `4px solid ${k.color}`, borderRadius: 2, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: k.color, mb: 1 }}>
                    {React.cloneElement(k.icon, { sx: { fontSize: 18 } })}
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {k.label}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
                    {k.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Por mes */}
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarMonthIcon sx={{ fontSize: 16 }} />
              Por mes
            </Typography>
            {(data.por_mes || []).length === 0 ? (
              <Typography sx={{ fontSize: 13, color: '#94a3b8', py: 3, textAlign: 'center' }}>
                Sin charters en el período seleccionado.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Mes</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#334155' }} align="right">Cantidad</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#334155' }} align="right">Cancelados</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#334155' }} align="right">Ingresos confirmados</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#334155' }} align="right">Cobrados</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.por_mes.map(m => (
                      <TableRow key={m.mes}>
                        <TableCell sx={{ fontWeight: 600 }}>{fmtMes(m.mes)}</TableCell>
                        <TableCell align="right">{m.cantidad}</TableCell>
                        <TableCell align="right">
                          {m.cancelados > 0 ? (
                            <Chip size="small" label={m.cancelados}
                              sx={{ height: 20, fontSize: 11, bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700 }}
                            />
                          ) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#7c3aed' }}>
                          {fmtMoney(m.ingresos_confirmados)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#059669' }}>
                          {fmtMoney(m.ingresos_cobrados)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Por embarcación */}
          <Paper sx={{ p: 2.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <DirectionsBoatIcon sx={{ fontSize: 16 }} />
              Utilización por embarcación
            </Typography>
            {(data.por_embarcacion || []).length === 0 ? (
              <Typography sx={{ fontSize: 13, color: '#94a3b8', py: 3, textAlign: 'center' }}>
                Sin embarcaciones con charters confirmados en el período.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Embarcación</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#334155' }} align="right">Charters</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#334155' }} align="right">Días ocupada</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Utilización</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#334155' }} align="right">Ingresos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.por_embarcacion.map(e => (
                      <TableRow key={e.embarcacion_id}>
                        <TableCell sx={{ fontWeight: 600 }}>{e.nombre}</TableCell>
                        <TableCell align="right">{e.charters}</TableCell>
                        <TableCell align="right">{e.dias_ocupada} / {data.rango.dias}</TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress variant="determinate"
                              value={Math.min(e.utilizacion_pct, 100)}
                              sx={{
                                flex: 1, height: 8, borderRadius: 4,
                                bgcolor: '#f1f5f9',
                                '& .MuiLinearProgress-bar': { bgcolor: '#7c3aed', borderRadius: 4 },
                              }}
                            />
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', minWidth: 40 }}>
                              {e.utilizacion_pct}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#7c3aed' }}>
                          {fmtMoney(e.ingresos_confirmados)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}
